import React, { useState, useEffect } from 'react';
import { X, Plus } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { athleteAPI } from '../services/api';
import { useResponsive } from '../hooks/useResponsive';
import Modal from './Modal';
import { TextInput } from './ui';

// #207 — coach contact validation. Empty is allowed (optional); a present but
// malformed email/phone is an error that blocks Save.
const isValidCoachEmail = (email: string): boolean =>
  email.trim() === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
const isValidCoachPhone = (phone: string): boolean => {
  const digits = phone.replace(/\D/g, '');
  return digits.length === 0 || digits.length === 10;
};

const teamErrorStyle: React.CSSProperties = {
  display: 'block',
  marginTop: '6px',
  fontSize: '13px',
  color: '#DC2626',
  fontFamily: 'Manrope, sans-serif',
};

interface TeamInfoData {
  schoolName: string;
  schoolJerseyNumber: string;
  headCoachName: string;
  headCoachEmail: string;
  headCoachPhone: string;
  assistantCoaches: Array<{
    name: string;
    email: string;
    phone: string;
  }>;
  travelClubName: string;
  travelClubJerseyNumber: string;
  travelClubHeadCoachName: string;
  travelClubHeadCoachEmail: string;
  travelClubHeadCoachPhone: string;
  travelClubAssistantCoaches: Array<{
    name: string;
    email: string;
    phone: string;
  }>;
}

interface TeamInfoEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData: {
    school_info?: {
      name?: string;
      jerseyNumber?: string;
      coachingStaff?: Array<{
        name: string;
        email: string;
        phone?: string;
        position?: string;
      }>;
    };
    travel_club_info?: {
      name?: string;
      jerseyNumber?: string;
      coachingStaff?: Array<{
        name: string;
        email: string;
        phone?: string;
        position?: string;
      }>;
    };
  };
  onUpdate: (data: any) => void;
  athleteId: string;
  /** Render inside the unified ProfileEditModal (no own overlay). */
  embedded?: boolean;
  /** Called after a successful save. Falls back to onClose. */
  onSaved?: () => void;
}

const TeamInfoEditModal: React.FC<TeamInfoEditModalProps> = ({
  isOpen,
  onClose,
  initialData,
  onUpdate,
  athleteId,
  embedded = false,
  onSaved,
}) => {
  const { sessionToken } = useAuth();
  const { showToast } = useToast();
  const { isMobile } = useResponsive();
  const [isSaving, setIsSaving] = useState(false);

  // Helper functions to extract coach data
  const getHeadCoach = (coachingStaff?: Array<any>) => {
    if (!coachingStaff || !Array.isArray(coachingStaff) || coachingStaff.length === 0) return null;
    return coachingStaff.find(coach => coach.position === 'head_coach' && coach.isCurrent) || coachingStaff[0];
  };

  const getAssistantCoaches = (coachingStaff?: Array<any>) => {
    if (!coachingStaff || !Array.isArray(coachingStaff)) return [];
    const headCoach = getHeadCoach(coachingStaff);
    if (!headCoach) return [];
    // Return all coaches except the head coach
    return coachingStaff.filter(coach => coach !== headCoach);
  };

  const formatPhoneNumber = (phone: string): string => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const [formData, setFormData] = useState<TeamInfoData>({
    schoolName: '',
    schoolJerseyNumber: '',
    headCoachName: '',
    headCoachEmail: '',
    headCoachPhone: '',
    assistantCoaches: [],
    travelClubName: '',
    travelClubJerseyNumber: '',
    travelClubHeadCoachName: '',
    travelClubHeadCoachEmail: '',
    travelClubHeadCoachPhone: '',
    travelClubAssistantCoaches: [],
  });

  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  // #207 — inline validation errors for head-coach email/phone.
  const [contactErrors, setContactErrors] = useState<Record<string, string>>({});

  // Assistant coach modal state
  const [assistantCoachModalOpen, setAssistantCoachModalOpen] = useState(false);
  const [assistantCoachType, setAssistantCoachType] = useState<'school' | 'travel'>('school');
  const [assistantCoachForm, setAssistantCoachForm] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [assistantCoachErrors, setAssistantCoachErrors] = useState<{ email?: string; phone?: string }>({});

  const validateContactField = (field: string, value: string, kind: 'email' | 'phone') => {
    let err = '';
    if (kind === 'email' && !isValidCoachEmail(value)) err = 'Enter a valid email address';
    if (kind === 'phone' && !isValidCoachPhone(value)) err = 'Enter a valid 10-digit phone number';
    setContactErrors(prev => ({ ...prev, [field]: err }));
  };

  useEffect(() => {
    if (isOpen && initialData) {
      const schoolHeadCoach = getHeadCoach(initialData.school_info?.coachingStaff);
      const travelHeadCoach = getHeadCoach(initialData.travel_club_info?.coachingStaff);

      setFormData({
        schoolName: initialData.school_info?.name || '',
        schoolJerseyNumber: initialData.school_info?.jerseyNumber || '',
        headCoachName: schoolHeadCoach?.name || '',
        headCoachEmail: schoolHeadCoach?.email || '',
        headCoachPhone: formatPhoneNumber(schoolHeadCoach?.phone || ''),
        assistantCoaches: getAssistantCoaches(initialData.school_info?.coachingStaff).map(coach => ({
          name: coach.name || '',
          email: coach.email || '',
          phone: formatPhoneNumber(coach.phone || '')
        })),
        travelClubName: initialData.travel_club_info?.name || '',
        travelClubJerseyNumber: initialData.travel_club_info?.jerseyNumber || '',
        travelClubHeadCoachName: travelHeadCoach?.name || '',
        travelClubHeadCoachEmail: travelHeadCoach?.email || '',
        travelClubHeadCoachPhone: formatPhoneNumber(travelHeadCoach?.phone || ''),
        travelClubAssistantCoaches: getAssistantCoaches(initialData.travel_club_info?.coachingStaff).map(coach => ({
          name: coach.name || '',
          email: coach.email || '',
          phone: formatPhoneNumber(coach.phone || '')
        })),
      });
      setHasChanges(false);
      setContactErrors({});
    }
  }, [isOpen, initialData]);

  const updateFormData = (field: keyof TeamInfoData, value: string) => {
    setHasChanges(true);
    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      // Auto-format phone number as user types
      if (field === 'headCoachPhone' || field === 'travelClubHeadCoachPhone') {
        const cleaned = value.replace(/\D/g, '');
        if (cleaned.length <= 10) {
          if (cleaned.length === 10) {
            updated[field] = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
          } else if (cleaned.length > 6) {
            updated[field] = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
          } else if (cleaned.length > 3) {
            updated[field] = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
          } else {
            updated[field] = cleaned;
          }
        }
      }

      return updated;
    });
  };

  // Assistant coach modal functions
  const openAssistantCoachModal = (type: 'school' | 'travel') => {
    setAssistantCoachType(type);
    setAssistantCoachForm({ name: '', email: '', phone: '' });
    setAssistantCoachErrors({});
    setAssistantCoachModalOpen(true);
  };

  const closeAssistantCoachModal = () => {
    setAssistantCoachModalOpen(false);
    setAssistantCoachForm({ name: '', email: '', phone: '' });
    setAssistantCoachErrors({});
  };

  const addAssistantCoach = () => {
    if (!assistantCoachForm.name.trim()) {
      showToast('Please enter the coach name', 'warning');
      return;
    }

    // #207 — reject clearly-invalid assistant coach contact info.
    const nextErrors: { email?: string; phone?: string } = {};
    if (!isValidCoachEmail(assistantCoachForm.email)) nextErrors.email = 'Enter a valid email address';
    if (!isValidCoachPhone(assistantCoachForm.phone)) nextErrors.phone = 'Enter a valid 10-digit phone number';
    if (nextErrors.email || nextErrors.phone) {
      setAssistantCoachErrors(nextErrors);
      return;
    }

    setHasChanges(true);
    const newCoach = {
      name: assistantCoachForm.name.trim(),
      email: assistantCoachForm.email.trim(),
      phone: assistantCoachForm.phone.trim(),
    };

    if (assistantCoachType === 'school') {
      setFormData(prev => ({
        ...prev,
        assistantCoaches: [...prev.assistantCoaches, newCoach],
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        travelClubAssistantCoaches: [...prev.travelClubAssistantCoaches, newCoach],
      }));
    }

    closeAssistantCoachModal();
  };

  const removeAssistantCoach = (type: 'school' | 'travel', index: number) => {
    setHasChanges(true);
    if (type === 'school') {
      setFormData(prev => ({
        ...prev,
        assistantCoaches: prev.assistantCoaches.filter((_, i) => i !== index),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        travelClubAssistantCoaches: prev.travelClubAssistantCoaches.filter((_, i) => i !== index),
      }));
    }
  };

  const handleSave = async () => {
    // Validate that at least school name is provided
    if (!formData.schoolName.trim()) {
      showToast('Please enter your school name', 'error');
      return;
    }

    if (!sessionToken || !athleteId) {
      showToast('Authentication error. Please try again.', 'error');
      console.error('Missing auth data:', { sessionToken: !!sessionToken, athleteId });
      return;
    }

    // #207 — validate coach contact so garbage isn't saved as a coach's email/phone.
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const digitsOnly = (p: string) => p.replace(/\D/g, '');
    const allCoaches = [
      { email: formData.headCoachEmail, phone: formData.headCoachPhone },
      { email: formData.travelClubHeadCoachEmail, phone: formData.travelClubHeadCoachPhone },
      ...formData.assistantCoaches,
      ...formData.travelClubAssistantCoaches,
    ];
    for (const c of allCoaches) {
      if (c.email && c.email.trim() && !emailRe.test(c.email.trim())) {
        showToast('Please enter a valid coach email address', 'error');
        return;
      }
      if (c.phone && digitsOnly(c.phone).length > 0 && digitsOnly(c.phone).length !== 10) {
        showToast('Please enter a valid 10-digit coach phone number', 'error');
        return;
      }
    }

    setIsSaving(true);

    try {
      // #243 — removed console.logs that dumped coach emails/phones (PII) to the
      // browser console in production.
      // Save exactly like OnboardingSchoolInfo does - send formData directly
      const result = await athleteAPI.updateSchoolInfo(sessionToken, athleteId, formData);

      if (result.success) {
        showToast('Team info saved.', 'success');
        
        // Just call onUpdate to trigger parent to reload - parent will fetch fresh data
        onUpdate({});
        (onSaved || onClose)();
      } else {
        console.error('API Error:', result);
        showToast('Failed to update team information', 'error');
      }
    } catch (error: any) {
      console.error('Failed to save team info:', error);
      showToast('Failed to update team information. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      inline={embedded}
      isDirty={hasChanges}
      labelledById="team-title"
      zIndex={9999}
      contentStyle={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: isMobile ? 'calc(100vw - 32px)' : '100%',
        maxWidth: isMobile ? 'calc(100vw - 32px)' : '600px',
        maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      }}
    >
        {/* Header */}
        <div
          style={{
            padding: '24px 28px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2
            id="team-title"
            style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#151133',
              margin: 0,
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            Edit Team Information
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6B7280',
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#151133';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#6B7280';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            padding: '28px',
            flex: 1,
            overflowY: 'auto',
          }}
        >
          {/* Thirteen hand-styled boxes became TextInputs. They keep the
              focusedField border and glow, which is a real treatment that works
              for a keyboard as well as a mouse; what they lose is the inline
              `outline: none` that sat beside it and cancelled the global
              :focus-visible ring. The labels now point at their boxes — none of
              them had an htmlFor, so clicking a label did nothing and a screen
              reader read every field on this sheet unnamed. */}
          {/* High School Team Section */}
          <div style={{ marginBottom: '32px' }}>
            <h3
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#151133',
                marginBottom: '16px',
                paddingBottom: '8px',
                borderBottom: '2px solid #F9550D',
                fontFamily: 'Manrope, sans-serif',
              }}
            >
              High School Team
            </h3>

            {/* School Name */}
            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block',
                  fontFamily: 'Manrope, sans-serif',
                }}
                htmlFor="team-school-name"
              >
                School Name *
              </label>
              <TextInput
                id="team-school-name"
                type="text"
                value={formData.schoolName}
                onChange={(e) => updateFormData('schoolName', e.target.value)}
                onFocus={(e) => setFocusedField('schoolName')}
                onBlur={() => setFocusedField(null)}
                placeholder="Enter school name"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: focusedField === 'schoolName' ? '1px solid #F9550D' : '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  boxShadow: focusedField === 'schoolName' ? '0 0 0 3px rgba(249, 85, 13, 0.1)' : 'none',
                }}
              />
            </div>

            {/* Jersey Number */}
            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block',
                  fontFamily: 'Manrope, sans-serif',
                }}
                htmlFor="team-school-jersey-number"
              >
                Jersey Number
              </label>
              <TextInput
                id="team-school-jersey-number"
                type="text"
                value={formData.schoolJerseyNumber}
                onChange={(e) => updateFormData('schoolJerseyNumber', e.target.value)}
                onFocus={(e) => setFocusedField('schoolJerseyNumber')}
                onBlur={() => setFocusedField(null)}
                placeholder="Enter jersey number"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: focusedField === 'schoolJerseyNumber' ? '1px solid #F9550D' : '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  boxShadow: focusedField === 'schoolJerseyNumber' ? '0 0 0 3px rgba(249, 85, 13, 0.1)' : 'none',
                }}
              />
            </div>

            {/* Head Coach Section */}
            <h4
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#151133',
                marginBottom: '16px',
                fontFamily: 'Manrope, sans-serif',
              }}
            >
              Head Coach
            </h4>

            {/* Head Coach Name */}
            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block',
                  fontFamily: 'Manrope, sans-serif',
                }}
                htmlFor="team-head-coach-name"
              >
                Coach Name
              </label>
              <TextInput
                id="team-head-coach-name"
                type="text"
                value={formData.headCoachName}
                onChange={(e) => updateFormData('headCoachName', e.target.value)}
                onFocus={(e) => setFocusedField('headCoachName')}
                onBlur={() => setFocusedField(null)}
                placeholder="Enter coach name"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: focusedField === 'headCoachName' ? '1px solid #F9550D' : '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  boxShadow: focusedField === 'headCoachName' ? '0 0 0 3px rgba(249, 85, 13, 0.1)' : 'none',
                }}
              />
            </div>

            {/* Head Coach Email */}
            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block',
                  fontFamily: 'Manrope, sans-serif',
                }}
                htmlFor="team-head-coach-email"
              >
                Coach Email
              </label>
              <TextInput
                id="team-head-coach-email"
                type="email"
                value={formData.headCoachEmail}
                onChange={(e) => updateFormData('headCoachEmail', e.target.value)}
                onFocus={(e) => setFocusedField('headCoachEmail')}
                onBlur={() => { setFocusedField(null); validateContactField('headCoachEmail', formData.headCoachEmail, 'email'); }}
                placeholder="coach@school.edu"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: contactErrors.headCoachEmail
                    ? '1px solid #DC2626'
                    : focusedField === 'headCoachEmail' ? '1px solid #F9550D' : '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  boxShadow: focusedField === 'headCoachEmail' ? '0 0 0 3px rgba(249, 85, 13, 0.1)' : 'none',
                }}
              />
              {contactErrors.headCoachEmail && <span style={teamErrorStyle}>{contactErrors.headCoachEmail}</span>}
            </div>

            {/* Head Coach Phone */}
            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block',
                  fontFamily: 'Manrope, sans-serif',
                }}
                htmlFor="team-head-coach-phone"
              >
                Coach Phone
              </label>
              <TextInput
                id="team-head-coach-phone"
                type="tel"
                value={formData.headCoachPhone}
                onChange={(e) => updateFormData('headCoachPhone', e.target.value)}
                onFocus={(e) => setFocusedField('headCoachPhone')}
                onBlur={() => { setFocusedField(null); validateContactField('headCoachPhone', formData.headCoachPhone, 'phone'); }}
                placeholder="(555) 555-5555"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: contactErrors.headCoachPhone
                    ? '1px solid #DC2626'
                    : focusedField === 'headCoachPhone' ? '1px solid #F9550D' : '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  boxShadow: focusedField === 'headCoachPhone' ? '0 0 0 3px rgba(249, 85, 13, 0.1)' : 'none',
                }}
              />
              {contactErrors.headCoachPhone && <span style={teamErrorStyle}>{contactErrors.headCoachPhone}</span>}
            </div>

            {/* Assistant Coaches List */}
            {formData.assistantCoaches.map((coach, index) => (
              <div
                key={index}
                style={{
                  padding: '16px',
                  background: '#F9FAFB',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  border: '1px solid #E5E7EB',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#151133',
                      fontFamily: 'Manrope, sans-serif',
                    }}
                  >
                    Assistant Coach {index + 1}
                  </span>
                  <button
                    onClick={() => removeAssistantCoach('school', index)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#F9550D',
                      transition: 'opacity 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '0.7';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
                <div style={{ fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
                  {coach.name}
                </div>
                {coach.email && (
                  <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '4px' }}>
                    {coach.email}
                  </div>
                )}
                {coach.phone && (
                  <div style={{ fontSize: '14px', color: '#6B7280' }}>
                    {coach.phone}
                  </div>
                )}
              </div>
            ))}

            {/* Add Assistant Coach Button */}
            <button
              onClick={() => openAssistantCoachModal('school')}
              style={{
                width: '100%',
                padding: '12px',
                background: 'transparent',
                border: '1px dashed #F9550D',
                borderRadius: '8px',
                color: '#F9550D',
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: 'Manrope, sans-serif',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
                marginBottom: '0',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#FEF2F2';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <Plus size={16} />
              Add Assistant Coach
            </button>
          </div>

          {/* Travel/Club Team Section */}
          <div>
            <h3
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#151133',
                marginBottom: '16px',
                paddingBottom: '8px',
                borderBottom: '2px solid #F9550D',
                fontFamily: 'Manrope, sans-serif',
              }}
            >
              Travel/Club Team
            </h3>

            {/* Travel Club Name */}
            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block',
                  fontFamily: 'Manrope, sans-serif',
                }}
                htmlFor="team-travel-club-name"
              >
                Team Name
              </label>
              <TextInput
                id="team-travel-club-name"
                type="text"
                value={formData.travelClubName}
                onChange={(e) => updateFormData('travelClubName', e.target.value)}
                onFocus={(e) => setFocusedField('travelClubName')}
                onBlur={() => setFocusedField(null)}
                placeholder="Enter travel club name"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: focusedField === 'travelClubName' ? '1px solid #F9550D' : '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  boxShadow: focusedField === 'travelClubName' ? '0 0 0 3px rgba(249, 85, 13, 0.1)' : 'none',
                }}
              />
            </div>

            {/* Travel Jersey Number */}
            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block',
                  fontFamily: 'Manrope, sans-serif',
                }}
                htmlFor="team-travel-club-jersey-number"
              >
                Jersey Number
              </label>
              <TextInput
                id="team-travel-club-jersey-number"
                type="text"
                value={formData.travelClubJerseyNumber}
                onChange={(e) => updateFormData('travelClubJerseyNumber', e.target.value)}
                onFocus={(e) => setFocusedField('travelClubJerseyNumber')}
                onBlur={() => setFocusedField(null)}
                placeholder="Enter jersey number"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: focusedField === 'travelClubJerseyNumber' ? '1px solid #F9550D' : '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  boxShadow: focusedField === 'travelClubJerseyNumber' ? '0 0 0 3px rgba(249, 85, 13, 0.1)' : 'none',
                }}
              />
            </div>

            {/* Travel Head Coach Section */}
            <h4
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#151133',
                marginBottom: '16px',
                fontFamily: 'Manrope, sans-serif',
              }}
            >
              Head Coach
            </h4>

            {/* Travel Head Coach Name */}
            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block',
                  fontFamily: 'Manrope, sans-serif',
                }}
                htmlFor="team-travel-club-head-coach-name"
              >
                Coach Name
              </label>
              <TextInput
                id="team-travel-club-head-coach-name"
                type="text"
                value={formData.travelClubHeadCoachName}
                onChange={(e) => updateFormData('travelClubHeadCoachName', e.target.value)}
                onFocus={(e) => setFocusedField('travelClubHeadCoachName')}
                onBlur={() => setFocusedField(null)}
                placeholder="Enter coach name"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: focusedField === 'travelClubHeadCoachName' ? '1px solid #F9550D' : '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  boxShadow: focusedField === 'travelClubHeadCoachName' ? '0 0 0 3px rgba(249, 85, 13, 0.1)' : 'none',
                }}
              />
            </div>

            {/* Travel Head Coach Email */}
            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block',
                  fontFamily: 'Manrope, sans-serif',
                }}
                htmlFor="team-travel-club-head-coach-email"
              >
                Coach Email
              </label>
              <TextInput
                id="team-travel-club-head-coach-email"
                type="email"
                value={formData.travelClubHeadCoachEmail}
                onChange={(e) => updateFormData('travelClubHeadCoachEmail', e.target.value)}
                onFocus={(e) => setFocusedField('travelClubHeadCoachEmail')}
                onBlur={() => { setFocusedField(null); validateContactField('travelClubHeadCoachEmail', formData.travelClubHeadCoachEmail, 'email'); }}
                placeholder="coach@club.com"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: contactErrors.travelClubHeadCoachEmail
                    ? '1px solid #DC2626'
                    : focusedField === 'travelClubHeadCoachEmail' ? '1px solid #F9550D' : '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  boxShadow: focusedField === 'travelClubHeadCoachEmail' ? '0 0 0 3px rgba(249, 85, 13, 0.1)' : 'none',
                }}
              />
              {contactErrors.travelClubHeadCoachEmail && <span style={teamErrorStyle}>{contactErrors.travelClubHeadCoachEmail}</span>}
            </div>

            {/* Travel Head Coach Phone */}
            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block',
                  fontFamily: 'Manrope, sans-serif',
                }}
                htmlFor="team-travel-club-head-coach-phone"
              >
                Coach Phone
              </label>
              <TextInput
                id="team-travel-club-head-coach-phone"
                type="tel"
                value={formData.travelClubHeadCoachPhone}
                onChange={(e) => updateFormData('travelClubHeadCoachPhone', e.target.value)}
                onFocus={(e) => setFocusedField('travelClubHeadCoachPhone')}
                onBlur={() => { setFocusedField(null); validateContactField('travelClubHeadCoachPhone', formData.travelClubHeadCoachPhone, 'phone'); }}
                placeholder="(555) 555-5555"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: contactErrors.travelClubHeadCoachPhone
                    ? '1px solid #DC2626'
                    : focusedField === 'travelClubHeadCoachPhone' ? '1px solid #F9550D' : '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  boxShadow: focusedField === 'travelClubHeadCoachPhone' ? '0 0 0 3px rgba(249, 85, 13, 0.1)' : 'none',
                }}
              />
              {contactErrors.travelClubHeadCoachPhone && <span style={teamErrorStyle}>{contactErrors.travelClubHeadCoachPhone}</span>}
            </div>

            {/* Travel Assistant Coaches List */}
            {formData.travelClubAssistantCoaches.map((coach, index) => (
              <div
                key={index}
                style={{
                  padding: '16px',
                  background: '#F9FAFB',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  border: '1px solid #E5E7EB',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#151133',
                      fontFamily: 'Manrope, sans-serif',
                    }}
                  >
                    Assistant Coach {index + 1}
                  </span>
                  <button
                    onClick={() => removeAssistantCoach('travel', index)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#F9550D',
                      transition: 'opacity 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '0.7';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
                <div style={{ fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
                  {coach.name}
                </div>
                {coach.email && (
                  <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '4px' }}>
                    {coach.email}
                  </div>
                )}
                {coach.phone && (
                  <div style={{ fontSize: '14px', color: '#6B7280' }}>
                    {coach.phone}
                  </div>
                )}
              </div>
            ))}

            {/* Add Travel Assistant Coach Button */}
            <button
              onClick={() => openAssistantCoachModal('travel')}
              style={{
                width: '100%',
                padding: '12px',
                background: 'transparent',
                border: '1px dashed #F9550D',
                borderRadius: '8px',
                color: '#F9550D',
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: 'Manrope, sans-serif',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
                marginBottom: '0',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#FEF2F2';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <Plus size={16} />
              Add Assistant Coach
            </button>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '20px 28px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '12px',
          }}
        >
          <button
            onClick={onClose}
            disabled={isSaving}
            style={{
              padding: '12px 24px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              fontFamily: 'Manrope, sans-serif',
              color: '#374151',
              backgroundColor: 'white',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: isSaving ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isSaving) {
                e.currentTarget.style.backgroundColor = '#F9FAFB';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || Object.values(contactErrors).some(Boolean)}
            style={{
              padding: '12px 28px',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              fontFamily: 'Manrope, sans-serif',
              color: 'white',
              backgroundColor: (isSaving || Object.values(contactErrors).some(Boolean)) ? '#9CA3AF' : '#F9550D',
              cursor: (isSaving || Object.values(contactErrors).some(Boolean)) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: isSaving ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isSaving && !Object.values(contactErrors).some(Boolean)) {
                e.currentTarget.style.backgroundColor = '#E14D0B';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#F9550D';
            }}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
    </Modal>

      {/* Assistant Coach Modal */}
      {assistantCoachModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10010,
            padding: '20px',
          }}
          onClick={closeAssistantCoachModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <button
                onClick={closeAssistantCoachModal}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#6B7280',
                  fontFamily: 'Manrope, sans-serif',
                }}
              >
                Cancel
              </button>
              <h3
                style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  color: '#151133',
                  margin: 0,
                  fontFamily: 'Manrope, sans-serif',
                }}
              >
                Add Assistant Coach
              </h3>
              <button
                onClick={addAssistantCoach}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#F9550D',
                  fontFamily: 'Manrope, sans-serif',
                }}
              >
                Add
              </button>
            </div>

            {/* Modal Content */}
            <div
              style={{
                padding: '24px',
                flex: 1,
                overflowY: 'auto',
              }}
            >
              {/* Coach Name */}
              <div style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px',
                    display: 'block',
                    fontFamily: 'Manrope, sans-serif',
                  }}
                  htmlFor="team-assistant-coach-name"
                >
                  Coach Name *
                </label>
                <TextInput
                  id="team-assistant-coach-name"
                  type="text"
                  value={assistantCoachForm.name}
                  onChange={(e) => setAssistantCoachForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter coach name"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#F9550D';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(249, 85, 13, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#D1D5DB';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Coach Email */}
              <div style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px',
                    display: 'block',
                    fontFamily: 'Manrope, sans-serif',
                  }}
                  htmlFor="team-assistant-coach-email"
                >
                  Coach Email
                </label>
                <TextInput
                  id="team-assistant-coach-email"
                  type="email"
                  value={assistantCoachForm.email}
                  onChange={(e) => setAssistantCoachForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Enter coach email (optional)"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: `1px solid ${assistantCoachErrors.email ? '#DC2626' : '#D1D5DB'}`,
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#F9550D';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(249, 85, 13, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#D1D5DB';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
                {assistantCoachErrors.email && <span style={teamErrorStyle}>{assistantCoachErrors.email}</span>}
              </div>

              {/* Coach Phone */}
              <div style={{ marginBottom: '0' }}>
                <label
                  style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px',
                    display: 'block',
                    fontFamily: 'Manrope, sans-serif',
                  }}
                  htmlFor="team-assistant-coach-phone"
                >
                  Coach Phone
                </label>
                <TextInput
                  id="team-assistant-coach-phone"
                  type="tel"
                  value={assistantCoachForm.phone}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/\D/g, '');
                    let formatted = '';
                    if (cleaned.length <= 3) {
                      formatted = cleaned;
                    } else if (cleaned.length <= 6) {
                      formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
                    } else {
                      formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
                    }
                    setAssistantCoachForm(prev => ({ ...prev, phone: formatted }));
                  }}
                  placeholder="(555) 555-5555"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: `1px solid ${assistantCoachErrors.phone ? '#DC2626' : '#D1D5DB'}`,
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#F9550D';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(249, 85, 13, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#D1D5DB';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
                {assistantCoachErrors.phone && <span style={teamErrorStyle}>{assistantCoachErrors.phone}</span>}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TeamInfoEditModal;
